// apps/scheduler-worker/src/pages/TimePage.tsx
import { useMemo } from 'react';
import {
  Clock,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Loader2,
  WifiOff,
} from 'lucide-react';
import { format, isToday, isYesterday, parseISO, startOfDay } from 'date-fns';
import { formatDuration } from 'scheduler-lib';
import { useTimeEntries } from '../hooks/useTimeEntries';
import { useSyncService } from '../hooks/useSyncService';
import type { TimeEntryRecord } from '../lib/api';
import type { OfflineTimeEntry } from '../lib/db';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDateGroupLabel(dateStr: string): string {
  const date = parseISO(dateStr);
  if (isToday(date)) return 'Today';
  if (isYesterday(date)) return 'Yesterday';
  return format(date, 'EEEE, MMM d, yyyy');
}

function formatTime(timestamp: string): string {
  return format(parseISO(timestamp), 'h:mm a');
}

function computeDurationMinutes(
  startTime: string,
  endTime: string | null,
  durationMinutes: number | null
): number | null {
  if (durationMinutes != null && durationMinutes > 0) return durationMinutes;
  if (!endTime) return null;
  const diff = Math.round(
    (parseISO(endTime).getTime() - parseISO(startTime).getTime()) / 60000
  );
  return diff > 0 ? diff : null;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function SyncStatusBadge({ status }: { status: string }) {
  switch (status) {
    case 'synced':
    case 'created':
      return (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded-full">
          <CheckCircle className="h-3 w-3" />
          Synced
        </span>
      );
    case 'conflict':
      return (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700 bg-red-50 px-2 py-0.5 rounded-full">
          <AlertCircle className="h-3 w-3" />
          Conflict
        </span>
      );
    case 'pending':
      return (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-yellow-700 bg-yellow-50 px-2 py-0.5 rounded-full">
          <Clock className="h-3 w-3" />
          Pending
        </span>
      );
    case 'syncing':
      return (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full">
          <Loader2 className="h-3 w-3 animate-spin" />
          Syncing
        </span>
      );
    case 'error':
      return (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700 bg-red-50 px-2 py-0.5 rounded-full">
          <AlertCircle className="h-3 w-3" />
          Error
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center text-xs font-medium text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full">
          {status}
        </span>
      );
  }
}

function TimeEntryRow({ entry }: { entry: TimeEntryRecord }) {
  const duration = computeDurationMinutes(
    entry.startTime,
    entry.endTime,
    entry.durationMinutes
  );

  return (
    <div
      data-testid="time-entry-row"
      className="bg-white rounded-lg border border-gray-200 p-4"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-mono text-sm text-gray-600">
              {entry.partNumber || 'N/A'}
            </span>
            <SyncStatusBadge status={entry.syncStatus} />
          </div>
          <p className="text-sm font-medium text-gray-900">
            {entry.laborBucket || 'Unknown'}
          </p>
        </div>
        <div className="text-right text-sm text-gray-500 flex-shrink-0">
          {duration != null ? (
            <span className="font-medium text-gray-900">
              {formatDuration(duration)}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-green-600">
              <Loader2 className="h-3 w-3 animate-spin" />
              Active
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
        <Clock className="h-3.5 w-3.5" />
        <span>{formatTime(entry.startTime)}</span>
        {entry.endTime && (
          <>
            <span>-</span>
            <span>{formatTime(entry.endTime)}</span>
          </>
        )}
      </div>

      {entry.syncStatus === 'conflict' && entry.resolutionNotes && (
        <p className="mt-2 text-xs text-red-600 bg-red-50 rounded px-2 py-1">
          {entry.resolutionNotes}
        </p>
      )}
    </div>
  );
}

function PendingEntryRow({ entry }: { entry: OfflineTimeEntry }) {
  const duration = entry.endTime
    ? Math.round(
        (parseISO(entry.endTime).getTime() -
          parseISO(entry.startTime).getTime()) /
          60000
      )
    : null;

  return (
    <div
      data-testid="time-entry-row"
      className="bg-yellow-50 rounded-lg border border-yellow-200 p-4"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-mono text-sm text-gray-600">
              {entry.taskId.slice(0, 8)}...
            </span>
            <SyncStatusBadge status={entry.syncStatus} />
          </div>
          <p className="text-sm font-medium text-gray-900">Offline entry</p>
        </div>
        <div className="text-right text-sm text-gray-500 flex-shrink-0">
          {duration != null && duration > 0 ? (
            <span className="font-medium text-gray-900">
              {formatDuration(duration)}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-green-600">
              <Loader2 className="h-3 w-3 animate-spin" />
              Active
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
        <Clock className="h-3.5 w-3.5" />
        <span>{formatTime(entry.startTime)}</span>
        {entry.endTime && (
          <>
            <span>-</span>
            <span>{formatTime(entry.endTime)}</span>
          </>
        )}
      </div>

      {entry.syncError && (
        <p className="mt-2 text-xs text-red-600 bg-red-50 rounded px-2 py-1">
          {entry.syncError}
        </p>
      )}
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-4 p-4">
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          className="bg-white rounded-lg border border-gray-200 p-4 animate-pulse"
        >
          <div className="flex items-start justify-between">
            <div className="space-y-2 flex-1">
              <div className="h-4 bg-gray-200 rounded w-24" />
              <div className="h-4 bg-gray-200 rounded w-32" />
            </div>
            <div className="h-4 bg-gray-200 rounded w-16" />
          </div>
          <div className="h-3 bg-gray-200 rounded w-40 mt-3" />
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function TimePage() {
  const { data, isLoading, isError } = useTimeEntries();
  const { isOnline, pendingCount, triggerSync } = useSyncService();

  const entries = data?.entries ?? [];
  const pendingEntries = data?.pending ?? [];

  // Group server entries by date
  const groupedEntries = useMemo(() => {
    const groups = new Map<string, TimeEntryRecord[]>();
    for (const entry of entries) {
      const dayKey = startOfDay(parseISO(entry.startTime)).toISOString();
      const group = groups.get(dayKey);
      if (group) {
        group.push(entry);
      } else {
        groups.set(dayKey, [entry]);
      }
    }
    return groups;
  }, [entries]);

  return (
    <div className="flex flex-col min-h-0">
      {/* Sticky sync header */}
      <div
        data-testid="sync-header"
        className="sticky top-0 z-10 bg-white border-b border-gray-200 px-4 py-3"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-semibold text-gray-900">
              Time Tracking
            </h1>
            {!isOnline && (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-orange-700 bg-orange-50 px-2 py-0.5 rounded-full">
                <WifiOff className="h-3 w-3" />
                Offline
              </span>
            )}
          </div>
          {pendingCount > 0 && (
            <button
              data-testid="sync-now-button"
              onClick={() => triggerSync()}
              disabled={!isOnline}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 disabled:text-gray-400 disabled:cursor-not-allowed"
            >
              <RefreshCw className="h-4 w-4" />
              Sync ({pendingCount})
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <LoadingSkeleton />
        ) : isError ? (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
            <div className="text-red-400 mb-4">
              <AlertCircle className="h-12 w-12" />
            </div>
            <h3 className="text-lg font-medium text-gray-900">
              Failed to load time entries
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Check your connection and try again.
            </p>
          </div>
        ) : entries.length === 0 && pendingEntries.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
            <div className="text-gray-400 mb-4">
              <Clock className="h-12 w-12" />
            </div>
            <h3 className="text-lg font-medium text-gray-900">
              No time entries
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Start working on a task to record time.
            </p>
          </div>
        ) : (
          <div className="p-4 space-y-6">
            {/* Pending offline entries */}
            {pendingEntries.length > 0 && (
              <div>
                <h2 className="text-sm font-medium text-yellow-800 mb-2">
                  Pending Sync ({pendingEntries.length})
                </h2>
                <div className="space-y-2">
                  {pendingEntries.map((entry) => (
                    <PendingEntryRow
                      key={entry.clientEntryId}
                      entry={entry}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Server entries grouped by date */}
            {Array.from(groupedEntries.entries()).map(
              ([dateKey, dateEntries]) => (
                <div key={dateKey}>
                  <h2 className="text-sm font-medium text-gray-700 mb-2">
                    {formatDateGroupLabel(dateKey)}
                  </h2>
                  <div className="space-y-2">
                    {dateEntries.map((entry) => (
                      <TimeEntryRow key={entry.id} entry={entry} />
                    ))}
                  </div>
                </div>
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
}
